
import { useContext, useEffect, useState, useRef } from "react";
import "../../pages/navbar.css";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { clientConfig } from "../../awsClientConfig";
import { userProfileData } from "../../Common/profileInfo";
import { GetProfile } from "../../queries/getProfile";
import Message from "../Message";
import Loading from "../../Common/Loading";
import { useNavigate } from "react-router-dom";
import TaxPreparationServiceFormContext from "./TaxPreparationServiceFormContext";
import { PurchaseTaxReturn } from "../../queries/PurchaseTaxReturn";
import useEnrollmentStore from "../../store/EnrollmentStore";
import AppProgressBar from "../../Common/progressBar";
import { paths } from "../../paths";
import Footer from "../footer";

export function PaymentDetailsV2() {
  const { taxPreparationData, setTaxPreparationData, navigateToPage, completedPagesHandler, quoteId, quotePrice } = useContext(TaxPreparationServiceFormContext);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("");
  const [isSpreedlyInitialized, setIsSpreedlyInitialized] = useState(false); // Track Spreedly initialization
  const { taxPrepEnrollment, setTaxPrepEnrollment } = useEnrollmentStore();
  const navigate = useNavigate();
  const windowValue: any = window;
  const environmentKey = process.env.REACT_APP_ENVIRONMENTKEY;
  const paidRef = useRef(false); 

  const bottom_description = `Your tax filing is personalized based on the information you've entered. Upon clicking 'Continue', your payment method will be charged the amount of $${quotePrice || ''}`;
  const options = {
    company_name: 'TAX FILING',
    sidebar_bottom_description: bottom_description,
    amount: quotePrice ? `$${quotePrice}` : '',
    submit_label: 'Continue'
  };

  const client = new clientConfig();

  const unloadSpreedly = () => {
    if (windowValue.SpreedlyExpress && isSpreedlyInitialized) {
      try {
        console.log("Attempting to unload SpreedlyExpress...");
        windowValue.SpreedlyExpress.unload();
        setIsSpreedlyInitialized(false);
        console.log("SpreedlyExpress unloaded successfully.");
      } catch (err) {
        console.error("Error unloading SpreedlyExpress:", err);
      }
    } else {
      console.log("SpreedlyExpress not initialized or not available, skipping unload.");
    }
  };

  const closeView = () => {
    unloadSpreedly();
    navigateToPage("billingInfo");
  };

  const goToProtectionPlan = () => {
    unloadSpreedly();
  };

  const handlePaymentToken = async (token: any, formdata: any) => {
    if (!paidRef.current) { 
      setLoading(true);
      paidRef.current = true; 
      const profileId = await userProfileData();
      const variables = {
        PaymentMethodToken: token,
        ProfileId: profileId,
        TaxPrepQuoteId: quoteId
      };
      client
        .mutate({ mutation: PurchaseTaxReturn, variables: variables })
        .then((res: any) => {
          if (res.data.PurchaseTaxReturn.Id) {
            setLoading(false);
            setSuccess("Payment Successful");
            unloadSpreedly(); 
            setTimeout(() => {
              setSuccess("");
              if(taxPreparationData.jointFiling === 'MarriedFilingJointly') {
                setTaxPrepEnrollment({
                  taxPrepEnrollmentCompleted: taxPrepEnrollment?.taxPrepEnrollmentCompleted,
                  showTaxPrepNextStepsModal: taxPrepEnrollment?.showTaxPrepNextStepsModal,
                  paymentCompleted: true
                });
                navigateToPage('applicationReview');
              }else {
                setTaxPrepEnrollment({
                  taxPrepEnrollmentCompleted: taxPrepEnrollment?.taxPrepEnrollmentCompleted,
                  showTaxPrepNextStepsModal: taxPrepEnrollment?.showTaxPrepNextStepsModal,
                  paymentCompleted: true
                });
                completedPagesHandler('completed');
                navigateToPage('completed');
              }
            }, 5000);
          }
        })
        .catch((error: any) => {
          console.error("Payment error:", error); 
          setError("Error processing payment. Please try again.");
          setLoading(false);
          paidRef.current = false; 
          setTimeout(() => {
            setError("");
            if (!isSpreedlyInitialized) {
              initializeSpreedly();
            }
          }, 5000);
        });
    }
  };

  const enableButton = () => {
    setLoading(false);
    setIsSpreedlyInitialized(true); 
    windowValue.SpreedlyExpress.openView();
  };

  const initializeSpreedly = async () => {
    if (isSpreedlyInitialized) {
      console.log("Spreedly already initialized, skipping initialization.");
      return;
    }

    console.log("Attempting to initialize Spreedly...");
    windowValue.SpreedlyExpress.onInit(enableButton);
    
    try {
      const profileId = await userProfileData();
      const res : any = await client.query({
        query: GetProfile,
        variables: { profileId: profileId },
      });

      if (res.data.GetProfile !== null) {
        windowValue.SpreedlyExpress.init(environmentKey, options, {
          address1: taxPreparationData.billingAddress.streetAddress,
          address2: taxPreparationData.billingAddress.streetAddress2,
          city: taxPreparationData.billingAddress.city,
          state: taxPreparationData.billingAddress.state,
          zip: taxPreparationData.billingAddress.zipCode,
          country: "US",
          email: res.data.GetProfile?.Email,
          phone_number: res.data.GetProfile?.Phone,
        });
        windowValue.Spreedly.on('ready', function() {
          windowValue.Spreedly.setPlaceholder('number', '');
          console.log("Spreedly ready event fired, placeholder set.");
        });
      } else {
        console.warn("User profile data not found for Spreedly initialization.");
        setError("User profile not found. Cannot initialize payment form.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Spreedly initialization error:", error);
      setError("Failed to initialize payment form. Please refresh the page.");
      setLoading(false);
    }

    windowValue.SpreedlyExpress.onPaymentMethod(handlePaymentToken);
  };

  useEffect(() => {
    initializeSpreedly();

    return () => {
      console.log("Component unmounting. Running Spreedly cleanup.");
      unloadSpreedly();
    };
  }, []); 

  useEffect(() => {
    let closeButton: HTMLElement | null = null;
    let choosePlanButton: HTMLElement | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const handleCloseButton = () => closeView();
    const handleChooseAnotherPlan = () => goToProtectionPlan();

    if (isSpreedlyInitialized) {
      console.log("Spreedly initialized. Attempting to attach button listeners...");
      timeoutId = setTimeout(() => {
        closeButton = document.getElementById("spreedly-close-button");
        choosePlanButton = document.getElementById("chooseAnotherPlan");

        if (closeButton) {
          closeButton.addEventListener("click", handleCloseButton);
          console.log("Attached listener to spreedly-close-button.");
        } else {
          console.log("spreedly-close-button not found after delay.");
        }
        if (choosePlanButton) {
          choosePlanButton.addEventListener("click", handleChooseAnotherPlan);
          console.log("Attached listener to chooseAnotherPlan.");
        } else {
          console.log("chooseAnotherPlan not found after delay.");
        }
      }, 750); 
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (closeButton) {
        closeButton.removeEventListener("click", handleCloseButton);
        console.log("Removed listener from spreedly-close-button.");
      }
      if (choosePlanButton) {
        choosePlanButton.removeEventListener("click", handleChooseAnotherPlan);
        console.log("Removed listener from chooseAnotherPlan.");
      }
    };
  }, [isSpreedlyInitialized]); 

  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log("beforeunload event detected.");
      unloadSpreedly();
    };
    const handlePopState = () => {
      console.log("popstate event detected.");
      unloadSpreedly();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isSpreedlyInitialized]); 

  return (
    <>
      <div className="page-container">
        <Container className="mt-3 mb-5">
          <Row className="justify-content-center">
            <Col md={10} className="content-box py-5 px-lg-5">
              {loading ? (
                <Loading />
              ) : (
                <div className="mx-lg-5">
                  <h1 className="content-box-heading">Payment Details</h1>
                  <p className="content-text">
                    Please provide your billing address
                  </p>
                  {error && (
                    <Message type="error" message={error}></Message>
                  )}
                  {success && (
                    <Message type="success" message="Payment Successful"></Message>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </>
  );
}
export function PaymentDetails() {
  const {taxPreparationData, setTaxPreparationData, navigateToPage,completedPagesHandler,quoteId, quotePrice  } = useContext(TaxPreparationServiceFormContext);
  var paid = false;
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("");
  const { taxPrepEnrollment, setTaxPrepEnrollment } = useEnrollmentStore();
  const navigate = useNavigate();
  const month = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const windowValue: any = window;
  const environmentKey = process.env.REACT_APP_ENVIRONMENTKEY;
  let bottom_description = `Your tax filing is personalized based on the information you\'ve entered. Upon clicking \'Continue\', your payment method will be charged the amount of $${quotePrice?quotePrice:''}`;
  const options = {
    company_name: 'TAX FILING',
    sidebar_bottom_description: bottom_description,
    amount: quotePrice?('$'+quotePrice):'',
    submit_label: 'Continue'
  };

  const client = new clientConfig();

  window.onbeforeunload = function (e) {
    windowValue.SpreedlyExpress.unload();
  };

  window.onpopstate = () => {
    windowValue.SpreedlyExpress.unload();
  };

  let doc: any = document;
  doc.getElementById("spreedly-close-button")?.addEventListener("click", () => {
    closeview();
  });
  doc.getElementById("chooseAnotherPlan")?.addEventListener("click", () => {
    goToProtectionPlan();
  });
  const closeview = () => {
    navigate("/tax-preparation-service");
    windowValue.SpreedlyExpress.unload();

  };

  const goToProtectionPlan = () => {
    windowValue.SpreedlyExpress.unload();

  };

  const handlePaymentToken = async (token: any, formdata: any) => {
    if (!paid) {
      setLoading(true);
      paid = true;
      const profileId = await userProfileData();
      const variables = {
        PaymentMethodToken: token,
        ProfileId: profileId,
        TaxPrepQuoteId: quoteId
      };
      client
        .mutate({ mutation: PurchaseTaxReturn, variables: variables })
        .then((res: any) => {
          if (res.data.PurchaseTaxReturn.Id) {
            setLoading(false);
            setSuccess("Payment Successful");
            setTimeout(() => {
              setSuccess("");
              if(taxPreparationData.jointFiling === 'MarriedFilingJointly') {
                //completedPagesHandler('completed');
                setTaxPrepEnrollment({
                  taxPrepEnrollmentCompleted: taxPrepEnrollment?.taxPrepEnrollmentCompleted,
                  showTaxPrepNextStepsModal: taxPrepEnrollment?.showTaxPrepNextStepsModal,
                  paymentCompleted: true
                });
                navigateToPage('applicationReview');
              }else {
                setTaxPrepEnrollment({
                  taxPrepEnrollmentCompleted: taxPrepEnrollment?.taxPrepEnrollmentCompleted,
                  showTaxPrepNextStepsModal: taxPrepEnrollment?.showTaxPrepNextStepsModal,
                  paymentCompleted: true
                });
                completedPagesHandler('completed');
                navigateToPage('completed');
              }
            }, 5000);
          }
        })
        .catch((error: any) => {
          console.log("error:", error);
          setError("error in payment method");
          setLoading(false);
          paid = false;
          setTimeout(() => {
            windowValue.SpreedlyExpress.unload();
            setError("");
            initializeSprredly();
          }, 5000);
        });
    }
    //completedPagesHandler('completed');
    //navigateToPage('completed');
  };

  const enableButton = () => {
    setLoading(false);
    windowValue.SpreedlyExpress.openView();
  };

  useEffect(() => {
    initializeSprredly();
  }, []);

  const initializeSprredly = async () => {
    windowValue.SpreedlyExpress.onInit(enableButton);
    const profileId = await userProfileData();
    client
      .query({
        query: GetProfile,
        variables: { profileId: profileId },
      })
      .then((res: any) => {
        if (res.data.GetProfile !== null) {
          windowValue.SpreedlyExpress.init(environmentKey, options, {
            address1: taxPreparationData.billingAddress.streetAddress,
            address2: taxPreparationData.billingAddress.streetAddress2,
            city: taxPreparationData.billingAddress.city,
            state: taxPreparationData.billingAddress.state,
            zip: taxPreparationData.billingAddress.zipCode,
            country: "US",
            email: res.data.GetProfile?.Email,
            // data: "something",
            phone_number: res.data.GetProfile?.Phone,
          });
        }
        windowValue.Spreedly.on('ready', function() {
          windowValue.Spreedly.setPlaceholder('number', '');
        });
      })
      .catch((error: any) => {
        console.log(error, "error");
      });

    windowValue.SpreedlyExpress.onPaymentMethod(handlePaymentToken);
  };

  return (
    <>
      <Container className="mt-3 mb-5">
        <Row className="justify-content-center">
          <Col md={10} className="content-box py-5  px-lg-5">
            {loading ? (
              <Loading />
            ) : (
              <div className="mx-lg-5">
                <AppProgressBar progressPercent={75} />
                <h1 className="content-box-heading">Payment Details</h1>
                <p className="content-text">
                  Please provide your billing address
                </p>
                {error && (
                  <Message type="error" message="Failed Payment"></Message>
                )}
                {success && (
                  <Message
                    type="success"
                    message="Payment Successful"
                  ></Message>
                )}
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
}
